import type { BaseLinkRoute } from '@connected/core';
import type { LinkProps } from '@tanstack/react-router';

import { useParams, useSearch } from '@tanstack/react-router';

import { Page } from '../../layouts';

// ============================================================================
// Types
// ============================================================================

export type HeaderNavigationProps<TContext> = {
    /**
     * Hook that returns the tabs for the current route.
     * Should return null if not on a tabbed page.
     */
    useHeaderNavigation: () => BaseLinkRoute<TContext>[] | null;
    /**
     * Function to render the label for a route.
     * Typically used for i18n translation.
     */
    renderLabel: (route: BaseLinkRoute<TContext>) => string;
};

// ============================================================================
// Component
// ============================================================================

/**
 * Generic header navigation component that renders Page.Header.Nav with tabs.
 * Works with any app's navigation system.
 *
 * @example
 * // In your app:
 * import { HeaderNavigation } from '@connected/ui/features/navigation';
 * import { useHeaderNavigation } from './hooks/use-navigation';
 *
 * function MyHeaderNavigation() {
 *     const { t } = useTranslation('navigation');
 *     return (
 *         <HeaderNavigation
 *             useHeaderNavigation={useHeaderNavigation}
 *             renderLabel={(route) => t(route.name)}
 *         />
 *     );
 * }
 */
export function HeaderNavigation<TContext>({
    useHeaderNavigation,
    renderLabel,
}: HeaderNavigationProps<TContext>) {
    const tabs = useHeaderNavigation();
    const params = useParams({ strict: false });
    const search = useSearch({ strict: false });

    if (!tabs || tabs.length === 0) {
        return null;
    }

    return (
        <Page.Header.Nav>
            {tabs.map(route => (
                <Page.Header.NavLink
                    key={route.path}
                    to={route.path as LinkProps['to']}
                    params={params}
                    search={search}
                >
                    {renderLabel(route)}
                </Page.Header.NavLink>
            ))}
        </Page.Header.Nav>
    );
}
